import type { TReviewLineItem } from "./types";

// Define fields for the edit modal
export type TLineItemFieldDef = {
    key: keyof TReviewLineItem;
    label: string;
    type: "text" | "number" | "dropdown";
    required?: boolean;
    alwaysReadOnly?: boolean;
    readOnlyWhenHasValue?: boolean;
    /**
     * Editable on split rows only. A split row's amount is the one value the
     * reviewer has to change — it starts at 0 and the row stays invalid until the
     * split adds up to the original — while amounts on ordinary rows come from the
     * source file and remain read-only.
     */
    editableOnSplitRows?: boolean;
    dropdownSource?: "propcode" | "coaCode" | "coaName";
};

/**
 * Fields that are mandatory on TB/GL/OTB lines but optional on FinJournal lines.
 * FinJournals are posted as-is, so a line without a mapped Propcode or Acc No is
 * still saveable and still valid — mirrors the FinJournal exemption in
 * `_missing_mapping_errors` (`core/services/file_processing/processing_service.py`).
 */
export const FINJOURNAL_OPTIONAL_FIELD_KEYS: ReadonlyArray<keyof TReviewLineItem> = [
    "propcode",
    "breAccountNumber",
];

export const lineItemFields: TLineItemFieldDef[] = [
    { key: "trannum", label: "Trannum", type: "text", alwaysReadOnly: true },
    {
        key: "propcode",
        label: "Propcode",
        type: "dropdown",
        dropdownSource: "propcode",
        required: true,
    },
    { key: "localPropcode", label: "Local Prop Code", type: "text", readOnlyWhenHasValue: true },
    { key: "sourceAccountNumber", label: "Source Acc No", type: "text", alwaysReadOnly: true },
    { key: "sourceAccountName", label: "Source Acc Name", type: "text", alwaysReadOnly: true },
    {
        key: "breAccountNumber",
        label: "Acc No",
        type: "dropdown",
        dropdownSource: "coaCode",
        required: true,
    },
    { key: "breAccountName", label: "Acc Name", type: "dropdown", dropdownSource: "coaName" },
    { key: "amount", label: "Amount", type: "text", editableOnSplitRows: true },
    { key: "debit", label: "Debit", type: "text", alwaysReadOnly: true },
    { key: "credit", label: "Credit", type: "text", alwaysReadOnly: true },
    { key: "transactionDate", label: "Date", type: "text", alwaysReadOnly: true },
    { key: "postMonth", label: "Post Month", type: "text", alwaysReadOnly: true },
    { key: "bookNumber", label: "Book Number", type: "number", alwaysReadOnly: true },
    { key: "description", label: "Detail Notes", type: "text" },
    { key: "ref", label: "Ref", type: "text", alwaysReadOnly: true },
    { key: "exchangeRate", label: "Exchange Rate", type: "text" },
    { key: "exchangeRateDate", label: "Exchange Rate Date", type: "text" },
    { key: "displayType", label: "Display Type", type: "text" },
    { key: "localCurrency", label: "Local Currency", type: "text" },
    { key: "localAmount", label: "Local Amount", type: "text" },
];

/**
 * Edit-modal field definitions for a file of `fileType`.
 *
 * Identical to `lineItemFields` except on FinJournals, where Propcode and Acc No
 * carry no required flag — so the modal shows no asterisk and the save is not
 * blocked when they are blank.
 */
export function resolveLineItemFields(fileType: string | undefined): TLineItemFieldDef[] {
    if (fileType !== "financial_journals") return lineItemFields;
    return lineItemFields.map((field) =>
        FINJOURNAL_OPTIONAL_FIELD_KEYS.includes(field.key) ? { ...field, required: false } : field,
    );
}
